import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import StatusPill from '../StatusPill'
import WindowChip from '../WindowChip'
import { AvisosCtx } from '../../useAvisos'
import apiClient from '../../api/client'
import { unirMensajes } from '../../hilo'
import { comoSeDibuja } from '../../burbujas'
import { estadoDeEntrega } from '../../entrega'
import Adjunto from './Adjunto'
import Confirmacion from './Confirmacion'
import {
  CLASES_TONO, clienteNoRespondioAlAsesor, estadoAtencion, preguntaAbiertaAlCliente,
  puedeVolverAlAgente,
} from '../../atencion'
import type { ConversationDetail, Message } from '../../types/portal'
import { telefonoVisible } from '../../identidad'
import { fechaHoraEnLima } from '../../documentos'
import {
  HORAS_DE_LA_SESION, avisoDeFinDeSesion, avisoDeSesionDevuelta, despuesDeLaVentana,
  ventanaCerradaConSocio, type AvisoDeSesionDevuelta, type AvisoFinDeSesion,
} from '../../sesionSocio'

/** El alto máximo del compositor: el `max-h-[110px]` de su clase. */
const ALTO_MAXIMO_COMPOSITOR = 110

/**
 * Columna 2 de la bandeja — el chat de WhatsApp.
 *
 * Cabecera con el estado y la ventana de 24 h · hilo · sugerencia de IA ·
 * plantillas rápidas · compositor. Cuando la ventana está cerrada el
 * compositor y las plantillas rápidas se OCULTAN y en su lugar aparece el
 * bloque bloqueado: dejar el textarea habilitado invita a escribir un mensaje
 * que Meta va a rechazar con 131047 `outside_window`.
 *
 * Antes que la ventana manda la sesión con el socio: devuelta a Software, el
 * número es suyo y lo escrito no le llega al cliente aunque la ventana diga
 * «abierta» (`sesionSocio.ts`, TEST 06-oct-2026).
 */

export interface QuickTemplate {
  key: string
  label: string
  body: string
}

/** Acciones de la barra de monitoreo, solo en modo solo lectura. */
export interface MonitorActions {
  onIntervene: () => void
  onReassign: () => void
  busy: boolean
  /** La conversación ya está asignada a quien mira: no hay nada que tomar, y
   *  el botón lleva a donde se le puede escribir. */
  esMia?: boolean
}

interface ChatThreadProps {
  detail: ConversationDetail
  /**
   * `message_id` del último mensaje del cliente que llegó ESTANDO la
   * conversación abierta. Su burbuja entra con animación; las que ya estaban
   * al abrir el hilo, no. Lo calcula `useAvisoMensajes`.
   */
  recienLlegado?: string | null
  /**
   * Solo lectura: es la pestaña "En vivo" del supervisor. Sin compositor, sin
   * plantillas, sin sugerencia de IA y sin resolver — en su lugar va la barra
   * de monitoreo. La sugerencia de IA se oculta a propósito: es una
   * herramienta del asesor que atiende, no del que audita.
   */
  readOnly?: boolean
  monitor?: MonitorActions
  /** Borrador del compositor. Vive en el padre: la sugerencia de IA y la
   *  acción recomendada del panel derecho también lo escriben. */
  draft: string
  onDraftChange: (value: string) => void
  /**
   * DEF-043: el borrador es un «Pedir de nuevo» y esto es lo que dice el chip
   * («Pidiendo de nuevo: Copia Literal · CL.pdf»). Con el chip, el mensaje
   * sale sin tomar la conversación; quitarlo (✕) vuelve al envío normal.
   */
  pedidoDeNuevo?: string | null
  /** Qué pasa al enviar con el chip puesto (`pedirDeNuevo.avisoDelPedido`). */
  avisoPedido?: string | null
  onQuitarPedido?: () => void
  onSend: () => void
  sending: boolean
  sendError: string | null
  suggestion: string | null
  onUseSuggestion: () => void
  onDismissSuggestion: () => void
  templates: QuickTemplate[]
  onResolve: () => void
  resolving: boolean
  onReactivate: () => void
  reactivating: boolean
  /** Solo supervisión y Admin, en su bandeja: salirse de la conversación
   *  dándosela a otro o devolviéndola a la cola. */
  onReassign?: () => void
  /** «Devolver al agente» (DEF-023): el agente IA retoma la conversación
   *  donde quedó. Sin esto el botón no se ofrece. */
  onReturnToAgent?: () => void
  returning?: boolean
  /** Extender la sesión del socio hasta 2 h (06-oct-2026). Sin esto no se
   *  ofrece: el aviso dice igual cuándo vuelve. */
  onExtendSession?: () => void
  extending?: boolean
}

/** Por qué «Devolver al agente» está deshabilitado con la ventana cerrada. */
const MOTIVO_VENTANA_CERRADA =
  'La ventana de 24 h de WhatsApp está cerrada: el agente no le puede escribir al '
  + 'cliente. Se habilita cuando el cliente vuelva a escribir.'

function horaCorta(ts: string): string {
  if (!ts) return ''
  return new Date(ts).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
}

function diaLegible(ts: string): string {
  const d = new Date(ts)
  const hoy = new Date()
  const ayer = new Date(hoy.getTime() - 86_400_000)
  const mismoDia = (a: Date, b: Date) => a.toDateString() === b.toDateString()
  if (mismoDia(d, hoy)) return 'HOY'
  if (mismoDia(d, ayer)) return 'AYER'
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'long' }).toUpperCase()
}

export default function ChatThread({
  detail, draft, onDraftChange, pedidoDeNuevo = null, avisoPedido = null, onQuitarPedido,
  onSend, sending, sendError,
  suggestion, onUseSuggestion, onDismissSuggestion,
  templates, onResolve, resolving, onReactivate, reactivating,
  readOnly = false, monitor, recienLlegado = null, onReassign,
  onReturnToAgent, returning = false, onExtendSession, extending = false,
}: ChatThreadProps) {
  // El contenedor que se desplaza: el hilo baja moviendo SU scroll, no
  // pidiéndole al navegador que lleve un elemento a la vista (DEF-031).
  const hiloRef = useRef<HTMLDivElement>(null)
  const [mostrarTodos, setMostrarTodos] = useState(false)
  // El historial anterior a la sesión en curso. El hilo se refresca con
  // `?vista=sesion` (solo la sesión) y esto se pide UNA vez, al tocar «Ver el
  // historial anterior»: lo viejo casi no cambia, y pedirlo cada 2,5 s era lo
  // que hacía pesar 233 KB a cada sondeo de una conversación larga.
  const [historial, setHistorial] = useState<Message[] | null>(null)
  const [cargandoHistorial, setCargandoHistorial] = useState(false)
  const [errorHistorial, setErrorHistorial] = useState<string | null>(null)
  const conversacionAbierta = useRef(detail.conversation_id)
  useEffect(() => {
    conversacionAbierta.current = detail.conversation_id
    setHistorial(null)
    setErrorHistorial(null)
    setMostrarTodos(false)
  }, [detail.conversation_id])
  // Qué acción espera confirmación. Vive acá y no en la bandeja porque es una
  // pregunta sobre lo que el asesor tiene delante: el hilo y su ventana.
  const [confirmando, setConfirmando] = useState<'devolver' | 'resolver' | null>(null)
  // La ventana emergente del fin de sesión sale UNA vez por fin: «Ahora no» la
  // cierra hasta que el fin cambie (se extendió, o el tope de la ventana se
  // movió porque el cliente escribió). El aviso sobre el compositor queda.
  const [finDescartado, setFinDescartado] = useState<string>('')

  // Al abrir una conversación y al llegar un mensaje, el hilo baja al final.
  //
  // Con su propio `scrollTop`, no con `scrollIntoView` (DEF-031, QA de TEST
  // 07-oct-2026): ése mueve TODOS los contenedores que se puedan mover hasta
  // que el elemento quede a la vista, también en horizontal, y con la página
  // más ancha que la ventana corría el documento a la derecha. El menú lateral
  // —con el usuario y «Salir»— quedaba fuera de la pantalla en cada mensaje.
  useEffect(() => {
    const hilo = hiloRef.current
    if (hilo) hilo.scrollTop = hilo.scrollHeight
  }, [detail.conversation_id, detail.messages.length])

  // El compositor crece con lo que muestra en un navegador SIN `field-sizing`
  // (DEF-031, QA de TEST 09/12-oct-2026). Con él lo resuelve el CSS. Sin él
  // (Firefox, Safari viejo) la caja quedaba en dos renglones y la ayuda,
  // cortada, con barra interna. Un mínimo fijo no sirve: medido con el CSS
  // compilado, a 210 px de campo la ayuda ocupa tres renglones y a 190 ocupa
  // cuatro. Se mide el contenido, con el mismo tope de 110 px de la clase.
  const campoRef = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const campo = campoRef.current
    if (!campo || CSS.supports?.('field-sizing', 'content')) return
    const ajustar = () => {
      campo.style.height = 'auto'
      // `scrollHeight` no cuenta el borde (1 px arriba y 1 abajo).
      campo.style.height = `${Math.min(campo.scrollHeight + 2, ALTO_MAXIMO_COMPOSITOR)}px`
    }
    ajustar()
    const fila = campo.parentElement
    if (!fila || typeof ResizeObserver === 'undefined') return
    // El ancho cambia con la ventana y con el cajón del panel, sin que cambie
    // el texto: también ahí hay que volver a medir.
    const observador = new ResizeObserver(ajustar)
    observador.observe(fila)
    return () => observador.disconnect()
  }, [draft])

  const ventana = detail.window
  const cerrada = !ventana.can_send_free_text
  // La sesión volvió a Software: ni el asesor ni el agente le pueden escribir
  // al cliente. Manda sobre la ventana, que en ese caso mide el número de ellos.
  const devuelta = avisoDeSesionDevuelta(detail)
  // Cuándo vuelve la sesión al socio, y si se puede extender (06-oct-2026).
  const finSesion = avisoDeFinDeSesion(detail)
  const claveFin = finSesion ? `${detail.conversation_id}|${detail.sesion_socio?.fin?.fin_at}` : ''
  // «Devolver al agente» se ofrece en manos humanas; con la ventana cerrada se
  // ve igual pero deshabilitado, con el motivo a la vista: el agente retoma
  // escribiéndole al cliente, y eso pide la ventana abierta.
  const volverAlAgente = Boolean(onReturnToAgent) && puedeVolverAlAgente(detail)

  // «Marcar resuelta» pregunta antes en dos casos, y los dos son el mismo
  // hecho: hay algo dicho que el cliente todavía no contestó, y resolver le
  // entrega su respuesta a un agente que arranca de cero.
  //   · DEF-004: un mensaje del asesor sin respuesta.
  //   · DEF-047: una pregunta abierta al cliente —«El cliente está
  //     decidiendo», §9.24—, que QA resolvió sin que nada se lo advirtiera.
  // Las dos reglas viven en `atencion.ts`; acá solo se elige el texto. En
  // cualquier otro caso se resuelve directo, como siempre.
  const preguntaAbierta = preguntaAbiertaAlCliente(detail)
  const pedirResolver = () => {
    if (preguntaAbierta || clienteNoRespondioAlAsesor(detail.messages)) {
      setConfirmando('resolver')
      return
    }
    onResolve()
  }
  // Abierta desde «Resueltas» (DEF-047): se lee, no se atiende. El servidor
  // tampoco deja responder sobre ella: no está asignada a nadie.
  const resuelta = detail.status === 'CLOSED'
  const ofrecerExtension = Boolean(
    !readOnly && !resuelta && onExtendSession && finSesion?.porVencer
    && finSesion.puedeExtender && finDescartado !== claveFin)

  // El hilo se abre en la SESIÓN EN CURSO, no en la conversación entera.
  //
  // Una conversación de WhatsApp con un cliente se acumula durante semanas:
  // sesiones cerradas por inactividad, documentos de hace un mes, un saludo de
  // ayer. Al asesor que entra a atender le sirve lo que se está hablando ahora;
  // lo anterior es contexto que consulta si lo necesita, y para eso está el
  // resumen del Copiloto, que lee la conversación completa.
  //
  // El corte es `session_started_at`, que escribe `conversation/handler` al
  // arrancar cada sesión. Si el backend no lo manda (conversaciones viejas) se
  // cae al tope de 60 mensajes, que era el comportamiento anterior.
  const TOPE = 60
  const inicioSesion = detail.session_started_at
  // Lo que el cliente habló con el precalificador del socio justo antes del
  // pase es parte de ESTA sesión aunque su hora sea anterior: es lo que la
  // precede, y el asesor tiene que leerlo sin abrir el historial.
  const desdeSesion = inicioSesion
    ? detail.messages.filter((m) => m.timestamp >= inicioSesion || m.del_pase_en_curso)
    : []
  // Una sesión sin mensajes propios no sirve de corte: mostraría un hilo vacío
  // con todo plegado detrás de un botón.
  // Los que el servidor dejó fuera por la vista de sesión; se piden al abrir
  // el historial. Sin el campo (una respuesta completa) son cero.
  const anterioresEnServidor = historial ? 0 : (detail.mensajes_anteriores ?? 0)
  const todos = historial ? unirMensajes(historial, detail.messages) : detail.messages
  const enPantalla = mostrarTodos
    ? todos
    : desdeSesion.length > 0
      ? desdeSesion
      : detail.messages.slice(-TOPE)
  // Las anotaciones del bot del lado del cliente no se dibujan (DEF-079): ver
  // `burbujas.ts`. Se cuentan igual en `ocultos`, que mide lo que quedó atrás.
  const mensajes = enPantalla.filter((m) => comoSeDibuja(m) !== 'nada')
  const ocultos = mostrarTodos
    ? anterioresEnServidor
    : detail.messages.length - enPantalla.length + anterioresEnServidor
  const cortadoPorSesion = !mostrarTodos && desdeSesion.length > 0 && ocultos > 0

  const verHistorial = async () => {
    setMostrarTodos(true)
    if (historial || cargandoHistorial || !detail.mensajes_anteriores) return
    const id = detail.conversation_id
    setCargandoHistorial(true)
    setErrorHistorial(null)
    try {
      const res = await apiClient.get<ConversationDetail>(
        `/conversations/${encodeURIComponent(id)}`)
      if (conversacionAbierta.current === id) setHistorial(res.data.messages)
    } catch {
      if (conversacionAbierta.current === id) {
        setErrorHistorial('No se pudo cargar el historial anterior. Intenta de nuevo.')
        setMostrarTodos(false)
      }
    } finally {
      setCargandoHistorial(false)
    }
  }

  return (
    <div className="flex min-h-0 min-w-[340px] flex-1 flex-col bg-white">
      {/* Cabecera de la conversación. Los botones bajan a un segundo renglón
          cuando no entran al lado del nombre (DEF-031, 07-oct-2026): con
          `shrink-0` se salían por la derecha con el chat en su mínimo, y
          el de resolver quedaba cortado. El nombre ocupa al menos 240 px
          antes de que los botones bajen. */}
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-surface-border px-[18px] py-3">
        <div className="min-w-0 grow basis-[240px]">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-[14.5px] font-extrabold text-ink">
              {detail.contact_name || 'Sin nombre'}
            </h2>
            <StatusPill status={detail.status} expediente={detail.expediente ?? null} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="font-mono text-micro text-ink-muted">{telefonoVisible(detail)}</span>
            {detail.basic_data?.dni && (
              <span className="font-mono text-micro text-ink-muted">
                DNI {detail.basic_data.dni}
              </span>
            )}
            <WindowChip window={ventana} sesionSocio={detail.sesion_socio} />
            {finSesion && <ChipFinDeSesion aviso={finSesion} />}
            <ChipAtencion detail={detail} />
          </div>
        </div>
        <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
        <BotonSonido />
        {readOnly ? (
          <span className="flex min-w-0 items-center gap-1.5 rounded-chip border border-info-border bg-info-light px-[13px] py-1.5 text-micro font-bold text-info">
            <OjoIcon />
            {/* «Asignada» no es «la atiende una persona»: desde el 01-oct-2026
                toda conversación queda asignada al generarse el checklist,
                también mientras la conduce el agente. */}
            {detail.assigned_to
              ? estadoAtencion(detail).quien === 'ia'
                ? `Solo lectura · asignada a ${detail.assigned_to.split('@')[0]} · conduce el agente IA`
                : `Solo lectura · atiende ${detail.assigned_to.split('@')[0]}`
              : 'Solo lectura · sin asesor asignado'}
          </span>
        ) : (
          <>
          {onReassign && detail.status !== 'CLOSED' && (
            <button
              onClick={onReassign}
              className="shrink-0 rounded-btn border border-surface-border bg-white px-[13px] py-2 text-body font-bold text-ink-soft transition-colors hover:border-brand hover:text-brand-hover"
              title="Salirte de la conversación: dársela a otro asesor o devolverla a la cola"
            >
              Reasignar
            </button>
          )}
          {volverAlAgente && (
            <div className="flex shrink-0 flex-col items-end">
              <button
                onClick={() => setConfirmando('devolver')}
                disabled={returning || cerrada || Boolean(devuelta)}
                className="shrink-0 rounded-btn border border-purple-border bg-purple-light px-[13px] py-2 text-body font-bold text-purple transition-colors hover:border-purple disabled:opacity-50"
                title={devuelta
                  ? `${devuelta.titulo}. ${devuelta.queHacer}`
                  : cerrada
                    ? MOTIVO_VENTANA_CERRADA
                    : 'El agente IA vuelve a responderle al cliente y sigue con los documentos desde donde quedó'}
              >
                {returning ? 'Devolviendo…' : '↩ Devolver al agente'}
              </button>
              {devuelta ? (
                <span className="mt-0.5 text-meta font-semibold text-ink-muted">
                  {devuelta.chip}
                </span>
              ) : cerrada && (
                <span className="mt-0.5 text-meta font-semibold text-warn-ink">
                  Ventana de 24 h cerrada
                </span>
              )}
            </div>
          )}
          <button
            onClick={pedirResolver}
            disabled={resolving || detail.status === 'CLOSED'}
            className="shrink-0 rounded-btn bg-brand px-[15px] py-2 text-body font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
          >
            {detail.status === 'CLOSED'
              ? 'Resuelta'
              : resolving ? 'Cerrando…' : '✓ Marcar resuelta'}
          </button>
          </>
        )}
        </div>
      </div>

      {/* Hilo */}
      <div ref={hiloRef} className="col-scroll scrollbar-thin flex-1 bg-surface-alt px-[18px] py-[18px]">
        {errorHistorial && (
          <p className="mb-2 text-center text-micro font-semibold text-danger">{errorHistorial}</p>
        )}
        {ocultos > 0 && (
          <div className="mb-3 text-center">
            <button onClick={() => void verHistorial()} disabled={cargandoHistorial} className="btn-row">
              {cargandoHistorial
                ? 'Cargando el historial…'
                : cortadoPorSesion
                  ? `Ver el historial anterior · ${ocultos} mensaje${ocultos === 1 ? '' : 's'}`
                  : `Ver los ${ocultos} mensajes anteriores`}
            </button>
            {cortadoPorSesion && (
              <p className="mt-1 text-meta text-ink-faint">
                Arriba está la conversación en curso. El resumen de todo lo
                hablado antes está en el panel Copiloto.
              </p>
            )}
          </div>
        )}
        <div className="flex flex-col gap-2.5">
          {mensajes.map((m, i) => {
            const anterior = mensajes[i - 1]
            const nuevoDia = !anterior ||
              new Date(anterior.timestamp).toDateString() !== new Date(m.timestamp).toDateString()
            const esPrecal = m.origen === 'precalificador'
            const previoPrecal = anterior?.origen === 'precalificador'
            return (
              <div key={m.message_id || `${m.timestamp}-${i}`}>
                {nuevoDia && m.timestamp && (
                  <div className="my-2 flex justify-center">
                    <span className="rounded-chip bg-surface-faint px-2.5 py-0.5 text-meta font-bold tracking-[0.06em] text-ink-muted">
                      {diaLegible(m.timestamp)}
                    </span>
                  </div>
                )}
                {esPrecal && !previoPrecal && (
                  <MarcaPrecalificador texto="Conversación con el precalificador del socio, antes del pase" />
                )}
                {!esPrecal && previoPrecal && (
                  <MarcaPrecalificador texto="El socio pasó la conversación al agente de documentos" />
                )}
                {comoSeDibuja(m) === 'evento'
                  ? <EventoSistema msg={m} />
                  : <Burbuja msg={m} nuevo={m.message_id === recienLlegado} />}
              </div>
            )
          })}
        </div>
      </div>

      {/* Compositor, o el bloque de ventana cerrada */}
      <div className="shrink-0 border-t border-surface-border px-[18px] py-3">
        {sendError && (
          <p className="mb-2 rounded-btn bg-danger-light px-3 py-2 text-micro text-danger">
            {sendError}
          </p>
        )}

        {readOnly ? (
          <>
          {devuelta && <BloqueSesionDevuelta aviso={devuelta} compacto />}
          <BarraMonitoreo
            asesor={detail.assigned_to}
            conduceIA={estadoAtencion(detail).quien === 'ia'}
            sinCanal={Boolean(devuelta)}
            onIntervene={monitor?.onIntervene ?? (() => {})}
            onReassign={monitor?.onReassign ?? (() => {})}
            busy={monitor?.busy ?? false}
            esMia={monitor?.esMia ?? false}
          />
          </>
        ) : resuelta && devuelta ? (
          // Resuelta Y en el precalificador: lo primero que hay que leer es
          // que ya no se le puede escribir, y por qué (06-oct-2026).
          <BloqueSesionDevuelta aviso={devuelta} nota={notaDeResuelta(detail)} />
        ) : resuelta ? (
          <BloqueResuelta resolvedAt={detail.resolved_at} resolvedBy={detail.resolved_by ?? null}
                          automatica={detail.resolucion_automatica ?? null} />
        ) : devuelta ? (
          <BloqueSesionDevuelta aviso={devuelta} />
        ) : cerrada ? (
          <BloqueVentanaCerrada
            nombre={detail.contact_name || 'El cliente'}
            desconocida={ventana.state === 'unknown'}
            onReactivate={onReactivate}
            reactivating={reactivating}
            sinVolverAlAgente={volverAlAgente}
            conSocio={ventanaCerradaConSocio(detail, detail.contact_name || 'El cliente',
                                             ventana.state === 'unknown')}
          />
        ) : (
          <>
            {finSesion?.porVencer && finSesion.enManosHumanas && (
              <AvisoFinSesion
                aviso={finSesion}
                onExtender={onExtendSession}
                extendiendo={extending}
              />
            )}

            {ventana.state === 'expiring' && (
              <p className="mb-2.5 rounded-btn border border-warn-border bg-warn-light px-3 py-2 text-micro text-warn-ink">
                Quedan <b>{ventana.time_left_label}</b> para responder libremente.{' '}
                {despuesDeLaVentana(detail)}
              </p>
            )}

            {suggestion && (
              <SugerenciaIA
                texto={suggestion}
                onUse={onUseSuggestion}
                onDismiss={onDismissSuggestion}
              />
            )}

            {templates.length > 0 && (
              <div className="scrollbar-thin mb-2.5 flex gap-1.5 overflow-x-auto pb-1">
                {templates.map((t) => (
                  <button
                    key={t.key}
                    title={t.body}
                    onClick={() => onDraftChange(t.body)}
                    className="shrink-0 rounded-chip border border-surface-border px-3 py-1.5 text-micro font-semibold text-ink-muted transition-colors hover:border-brand hover:bg-brand-light hover:text-brand-hover"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {pedidoDeNuevo ? (
              <ChipPedidoDeNuevo
                etiqueta={pedidoDeNuevo}
                aviso={avisoPedido}
                onQuitar={onQuitarPedido}
              />
            ) : (
              <AvisoAtencion detail={detail} />
            )}

            <div className="flex items-end gap-2.5">
              <SelectorEmoji onElegir={(e) => onDraftChange(draft + e)} />
              <textarea
                rows={2}
                value={draft}
                onChange={(e) => onDraftChange(e.target.value)}
                onKeyDown={(e) => {
                  // Enter envía, Shift+Enter salta de línea. Es la convención de
                  // WhatsApp y del prototipo; el asesor escribe todo el día acá.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    onSend()
                  }
                }}
                placeholder="Escribe tu respuesta… (Enter envía, Shift+Enter salta de línea)"
                // El campo crece con lo que muestra, el texto de ayuda incluido
                // (DEF-031, QA de TEST 09-oct-2026): con el chat en su ancho
                // mínimo —la franja de ~1150 a 1250 px— la ayuda ocupa tres
                // renglones, la caja se quedaba en dos y el asesor tenía que
                // desplazarse dentro de un campo vacío para leerla.
                // `field-sizing: content` lo resuelve sin JavaScript; el mínimo
                // es la altura de antes (dos renglones de 19,5 px + 20 de
                // relleno + 2 de borde) para que en una ventana ancha no cambie.
                // Sin `field-sizing`, mide `campoRef` (arriba).
                ref={campoRef}
                className="min-h-[61px] max-h-[110px] flex-1 resize-none rounded-input border border-surface-border px-3.5 py-2.5 text-chat text-ink [field-sizing:content] placeholder:text-ink-faint focus:border-brand focus:outline-none"
              />
              <button
                onClick={onSend}
                disabled={sending || !draft.trim()}
                className="shrink-0 rounded-input bg-brand px-5 py-2.5 text-chat font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
              >
                {sending ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </>
        )}
      </div>

      {ofrecerExtension && finSesion && (
        <Confirmacion
          titulo={finSesion.titulo}
          confirmar={finSesion.boton}
          cancelar="Ahora no"
          tono="warn"
          onCancelar={() => setFinDescartado(claveFin)}
          onConfirmar={() => { setFinDescartado(claveFin); onExtendSession?.() }}
        >
          {finSesion.queVaAPasar} Puedes extenderla hasta {
            duracionDeLaExtension(detail)} más, una sola vez.
          {finSesion.tope && <> {finSesion.tope}</>}
        </Confirmacion>
      )}

      {confirmando === 'devolver' && (
        <Confirmacion
          titulo="¿Devolver la conversación al agente?"
          confirmar="Devolver al agente"
          onCancelar={() => setConfirmando(null)}
          onConfirmar={() => { setConfirmando(null); onReturnToAgent?.() }}
        >
          El agente IA vuelve a responderle a {detail.contact_name || 'el cliente'} y
          sigue con los documentos desde donde quedó. Tú dejas de atenderla: si el
          cliente pide otra vez una persona, se deriva como siempre.
        </Confirmacion>
      )}

      {confirmando === 'resolver' && (
        <Confirmacion
          titulo={preguntaAbierta
            ? '¿Marcar resuelta con una pregunta abierta?'
            : '¿Marcar resuelta sin respuesta del cliente?'}
          confirmar="Marcar resuelta igual"
          tono="warn"
          onCancelar={() => setConfirmando(null)}
          onConfirmar={() => { setConfirmando(null); onResolve() }}
        >
          {!preguntaAbierta ? (
            <>El cliente todavía no respondió tu último mensaje. Si la marcas resuelta, su
              próximo mensaje lo atenderá el agente desde el inicio.</>
          ) : (
            <>Se le preguntó al cliente {preguntaAbierta} y todavía no contestó. Si la
              marcas resuelta, su respuesta no le llega a nadie: lo atenderá el agente
              desde el inicio.</>
          )}
          {volverAlAgente && (
            <> Si solo quieres que el agente siga con los documentos, usa
              «Devolver al agente».</>
          )}
        </Confirmacion>
      )}
    </div>
  )
}

/**
 * En lugar del compositor, sobre una conversación resuelta (DEF-047): la
 * bandeja la abre desde «Resueltas» para releerla, no para atenderla. Si el
 * cliente vuelve a escribir se reabre sola —y ahí vuelve el compositor—.
 */
function BloqueResuelta({ resolvedAt, resolvedBy, automatica = null }: {
  resolvedAt: string | null; resolvedBy: string | null
  /** Se cerró sola al volver la sesión al socio: no la resolvió `resolvedBy`. */
  automatica?: string | null
}) {
  const cuando = resolvedAt
    ? new Date(resolvedAt).toLocaleString('es-PE', {
        day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })
    : ''
  const quien = resolvedBy ? resolvedBy.split('@')[0] : ''
  return (
    <div className="rounded-input border border-surface-border bg-surface px-3.5 py-3 text-micro text-ink-muted">
      <p className="font-bold text-ink">
        {automatica
          ? <>Resuelta sola al volver la sesión al socio{cuando && ` · ${cuando}`}</>
          : <>Resuelta{quien && ` por ${quien}`}{cuando && ` · ${cuando}`}</>}
      </p>
      {automatica && (
        <p className="mt-1">
          Se cumplieron las {HORAS_DE_LA_SESION} h del pase{quien && ` mientras la atendía ${quien}`}: la
          conversación volvió al precalificador.
        </p>
      )}
      <p className="mt-1">
        Si el cliente vuelve a escribir, la conversación se reabre y vuelve a quien la
        resolvió, si sigue en la rotación; si no, a otro asesor.
      </p>
    </div>
  )
}

/** «2 h»: lo máximo que se puede extender, según el backend. */
function duracionDeLaExtension(detail: ConversationDetail): string {
  const min = detail.sesion_socio?.fin?.extension_max_min ?? 120
  return min % 60 === 0 ? `${min / 60} h` : `${min} min`
}

/**
 * Cuándo vuelve la sesión al socio. Gris mientras falta; ámbar con 30 min o
 * menos. Solo con la sesión todavía nuestra: devuelta, la dice `WindowChip`.
 */
function ChipFinDeSesion({ aviso }: { aviso: AvisoFinDeSesion }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-chip border px-2 py-0.5 text-pill font-bold ${
        aviso.porVencer
          ? 'border-warn-border bg-warn-light text-warn'
          : 'border-surface-border bg-surface text-ink-muted'
      }`}
      title={`${aviso.titulo}. ${aviso.queVaAPasar}`}
    >
      <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
        <path d="M4.5 3 2 5.5 4.5 8M2.4 5.5h5.1a2.5 2.5 0 0 1 0 5H6"
              stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {aviso.chip}
    </span>
  )
}

/**
 * Sobre el compositor, con 30 min o menos y una persona atendiendo: cuándo
 * vuelve la sesión al socio y el botón para extenderla. Queda aunque se cierre
 * la ventana emergente: es la misma oferta, sin interrumpir.
 */
function AvisoFinSesion({ aviso, onExtender, extendiendo }: {
  aviso: AvisoFinDeSesion
  onExtender?: () => void
  extendiendo: boolean
}) {
  return (
    <div className="mb-2.5 flex items-start justify-between gap-3 rounded-btn border border-warn-border bg-warn-light px-3 py-2 text-micro text-warn-ink">
      <div className="min-w-0">
        <p className="font-bold">{aviso.titulo}</p>
        <p className="mt-0.5">{aviso.queVaAPasar}</p>
        {aviso.tope && <p className="mt-0.5">{aviso.tope}</p>}
        {!aviso.puedeExtender && aviso.porQueNo && <p className="mt-0.5">{aviso.porQueNo}</p>}
      </div>
      {aviso.puedeExtender && onExtender && (
        <button
          onClick={onExtender}
          disabled={extendiendo}
          className="shrink-0 rounded-btn bg-warn px-3 py-1.5 text-micro font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {extendiendo ? 'Extendiendo…' : aviso.boton}
        </button>
      )}
    </div>
  )
}

/** Dónde empieza y termina lo hablado con el precalificador del socio. */
function MarcaPrecalificador({ texto }: { texto: string }) {
  return (
    <div className="my-1 flex justify-center">
      <span className="rounded-chip border border-info-border bg-info-light px-2.5 py-0.5 text-meta font-bold text-info">
        {texto}
      </span>
    </div>
  )
}

function EventoSistema({ msg }: { msg: Message }) {
  return (
    <div className="flex justify-center py-0.5">
      <span className="rounded-chip bg-surface px-2.5 py-0.5 text-meta text-ink-faint">
        {msg.text.replace(/^\[|\]$/g, '')}
        {msg.timestamp && ` · ${horaCorta(msg.timestamp)}`}
      </span>
    </div>
  )
}

/**
 * Emojis frecuentes en atención al cliente.
 *
 * Una lista corta y curada, no un catálogo. El asesor no necesita 1.800
 * emojis: necesita los seis que usa todo el día sin soltar el teclado. Un
 * selector completo son ~40 KB de datos y una grilla que hay que buscar, y el
 * teclado del sistema (Win + punto) ya cubre el caso raro.
 *
 * Están agrupados por lo que hacen en una conversación de cobranza y trámite:
 * saludar, confirmar, pedir paciencia, señalar un documento.
 */
const EMOJIS = [
  '🙂', '👋', '👍', '🙏', '✅', '📄',
  '📎', '⏳', '💬', '📱', '🎉', '❤️',
]

/**
 * «Pidiendo de nuevo: Copia Literal · CL.pdf ✕» (DEF-043). Va en lugar del
 * aviso de atención, que con el chip deja de ser cierto: el mensaje sale como
 * del asesor y el agente sigue conduciendo para recibir el reenvío.
 */
function ChipPedidoDeNuevo({ etiqueta, aviso, onQuitar }: {
  etiqueta: string; aviso: string | null; onQuitar?: () => void
}) {
  return (
    <div className="mb-2 flex flex-col gap-1">
      <div className="flex">
        <span className="inline-flex max-w-full items-center gap-1.5 rounded-chip border border-purple-border bg-purple-light px-2.5 py-1 text-micro font-bold text-purple">
          <span className="truncate">{etiqueta}</span>
          {onQuitar && (
            <button
              onClick={onQuitar}
              aria-label="Quitar el pedido de nuevo: el mensaje sale como una respuesta normal"
              title="Quitar: el mensaje sale como una respuesta normal y tomas la conversación"
              className="shrink-0 rounded-full px-1 leading-none hover:bg-white"
            >
              ✕
            </button>
          )}
        </span>
      </div>
      {aviso && <p className="text-meta text-ink-muted">{aviso}</p>}
    </div>
  )
}

/**
 * Aviso sobre el compositor cuando el agente IA todavía conduce.
 *
 * Va acá y no solo en la cabecera porque es el momento en que importa: el
 * asesor está por escribir y tiene que saber que ese mensaje cambia quién
 * atiende. Cuando ya atiende él no se muestra nada — un aviso permanente se
 * vuelve parte del fondo y deja de leerse.
 */
function AvisoAtencion({ detail }: { detail: ConversationDetail }) {
  const e = estadoAtencion(detail)
  if (e.quien !== 'ia' && e.quien !== 'eligiendo') return null
  return (
    <p className={`mb-2 rounded-card border px-3 py-2 text-body ${CLASES_TONO[e.tono]}`}>
      <b>{e.titulo}.</b> {e.detalle}
    </p>
  )
}

function SelectorEmoji({ onElegir }: { onElegir: (emoji: string) => void }) {
  const [abierto, setAbierto] = useState(false)

  // Cerrar al hacer clic fuera: sin esto el panel queda abierto tapando el
  // hilo mientras el asesor escribe.
  useEffect(() => {
    if (!abierto) return
    const cerrar = () => setAbierto(false)
    // `setTimeout` para no capturar el mismo clic que lo abrió.
    const id = setTimeout(() => document.addEventListener('click', cerrar), 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener('click', cerrar)
    }
  }, [abierto])

  return (
    <div className="relative shrink-0">
      {abierto && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-[52px] left-0 z-10 grid w-[196px] grid-cols-6 gap-0.5 rounded-card border border-surface-border bg-white p-2 shadow-lg"
        >
          {EMOJIS.map((e) => (
            <button
              key={e}
              onClick={() => { onElegir(e); setAbierto(false) }}
              className="rounded-btn py-1 text-[19px] leading-none transition-colors hover:bg-surface"
              aria-label={`Insertar ${e}`}
            >
              {e}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((v) => !v) }}
        title="Insertar emoji"
        aria-label="Insertar emoji"
        aria-expanded={abierto}
        className={`flex h-[44px] w-[42px] items-center justify-center rounded-input border transition-colors ${
          abierto
            ? 'border-brand bg-brand-light text-brand-hover'
            : 'border-surface-border text-ink-muted hover:bg-surface'
        }`}
      >
        <svg width="19" height="19" viewBox="0 0 20 20" fill="none" aria-hidden>
          <circle cx="10" cy="10" r="7.6" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="7.4" cy="8.4" r="1" fill="currentColor" />
          <circle cx="12.6" cy="8.4" r="1" fill="currentColor" />
          <path d="M6.9 12.2a3.7 3.7 0 0 0 6.2 0" stroke="currentColor"
                strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  )
}

// ── Burbuja ──────────────────────────────────────────────────────────────────

/**
 * Quién atiende, dicho sin rodeos.
 *
 * Antes la cabecera mostraba "Activa", que es lo mismo que dice cuando responde
 * el bot: la asesora no tenía forma de saber, antes de escribir, si el agente
 * iba a contestar también. Eso terminó en un cliente recibiendo el menú del bot
 * en medio de una conversación con una persona.
 *
 * El detalle explica qué pasa con el PRÓXIMO mensaje del cliente, que es la
 * pregunta real. El título solo no alcanza: "Responde el agente IA" sobre una
 * conversación asignada se lee como un error si no se aclara que escribir la
 * toma.
 */
function ChipAtencion({ detail }: { detail: ConversationDetail }) {
  const e = estadoAtencion(detail)
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-chip border px-2 py-0.5 text-pill font-bold ${CLASES_TONO[e.tono]}`}
      title={e.detalle}
    >
      {e.quien === 'ia' && <ChispaIA />}
      {e.titulo}
    </span>
  )
}

/** Chispa: marca lo que escribió la IA, no una persona. */
function ChispaIA() {
  return (
    <svg width="11" height="11" viewBox="0 0 17 17" fill="none" aria-hidden>
      <path d="M8.5 1 10.2 6 15 7.7l-4.8 1.7L8.5 15l-1.7-5.6L2 7.7l4.8-1.7z"
            fill="currentColor" />
    </svg>
  )
}

/**
 * Burbuja del hilo.
 *
 * LADO: entrante a la izquierda, saliente a la derecha. Es la convención de
 * cualquier herramienta de atención (WhatsApp Web, Intercom, Zendesk): quien
 * mira la pantalla es el equipo, así que "nosotros" va a la derecha.
 *
 * El handoff de diseño especificaba lo contrario —cliente a la derecha en
 * verde— porque estaba pensado desde la vista del cliente. Para el asesor se
 * lee al revés de lo que espera, así que se invirtió a pedido (01-set-2026).
 *
 * COLOR: dentro de "nosotros" hay que distinguir quién habló.
 *   cliente → gris claro, entrante y neutro
 *   bot     → violeta, que en TODO el portal significa IA (sugerencia,
 *             copiloto, confianza del clasificador), más la chispa y la
 *             etiqueta "IA": el asesor tiene que saber de un vistazo qué le
 *             dijo una máquina al cliente antes de contradecirla
 *   asesor  → verde primario, que es el color de la marca y del humano
 */
/**
 * Silenciar el aviso de mensaje nuevo.
 *
 * Va en la cabecera del hilo y no solo en el panel de «Avisos» porque es algo
 * que se cambia en el momento —una reunión, una llamada— y no una vez al mes.
 * Al encenderlo suena una vez: confirma que se oye y es el gesto que el
 * navegador exige para despertar el audio.
 *
 * Es el MISMO interruptor que el «Sonido» del panel (`useAvisos`). Hasta el
 * 06-oct-2026 tenía su propia clave y su propio tono: este podía decir
 * «encendido» con la campana del header silenciada, y la persona no sabía cuál
 * de los dos mandaba.
 */
function BotonSonido() {
  const avisos = useContext(AvisosCtx)
  if (!avisos?.estado) return null
  const activo = avisos.estado.sonido
  const alternar = () => { void avisos.cambiarSonido() }
  return (
    <button
      onClick={alternar}
      title={activo ? 'Silenciar el aviso de mensajes nuevos' : 'Activar el aviso de mensajes nuevos'}
      aria-label={activo ? 'Silenciar avisos' : 'Activar avisos'}
      aria-pressed={activo}
      className={`shrink-0 rounded-btn border px-2.5 py-2 transition-colors ${
        activo
          ? 'border-surface-border text-ink-muted hover:bg-surface'
          : 'border-warn-border bg-warn-light text-warn'
      }`}
    >
      {activo ? <IconoSonido /> : <IconoSilencio />}
    </button>
  )
}

function IconoSonido() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 2.5 4.8 5.2H2.5v5.6h2.3L8 13.5z" fill="currentColor" />
      <path d="M10.8 5.6a3.4 3.4 0 0 1 0 4.8M12.8 3.6a6.2 6.2 0 0 1 0 8.8"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function IconoSilencio() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 2.5 4.8 5.2H2.5v5.6h2.3L8 13.5z" fill="currentColor" />
      <path d="m10.8 6.2 3.6 3.6M14.4 6.2l-3.6 3.6"
            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

/**
 * El estado de entrega en palabras, para las burbujas que salieron de nuestro
 * lado: el asesor Y el agente.
 *
 * DEF-002 (QA, 01-oct-2026): Meta rechazó un mensaje del bot con 131056 (pair
 * rate limit) y el portal lo pintó igual que uno entregado, porque solo las
 * burbujas del asesor mostraban el estado. El asesor leía en el hilo una
 * respuesta que el cliente nunca recibió. La Lambda de conversación guarda en
 * los `MSG#` del agente los mismos campos que en los del asesor
 * (`delivery_status`, `failure_code`, `failure_title`, `failure_msg`), y acá
 * se pintan igual para los dos.
 *
 * Qué estado se dice —y qué significa «enviado» según por dónde salió— lo
 * decide `entrega.ts` (03-nov-2026): una burbuja sin estado se leía como «no
 * salió», y en PROD eran la mitad de los mensajes del bot, que sí habían salido.
 */

/** «No le llegó al cliente: <motivo> (código N)». El motivo es lo que Meta
 *  devolvió; sin él, al menos el código, que es lo que se busca en la doc. */
function motivoDelFallo(msg: Message): string {
  const motivo = (msg.failure_title || msg.failure_msg || '').trim()
  const codigo = (msg.failure_code || '').trim()
  if (!motivo && !codigo) return 'No le llegó al cliente.'
  return `No le llegó al cliente${motivo ? `: ${motivo}` : ''}${codigo ? ` (código ${codigo})` : ''}.`
}

function Burbuja({ msg, nuevo = false }: { msg: Message; nuevo?: boolean }) {
  // Lo hablado con el precalificador del socio: el cliente a la izquierda,
  // como siempre, y el precalificador a la derecha con su propio color, para
  // que no se confunda con nuestro agente ni con un asesor.
  const esPrecalBot = msg.sender === 'precal_bot'
  const esCliente = msg.sender === 'user' || msg.sender === 'precal_cliente'
  const esAsesor = msg.sender === 'operator'
  const esBot = !esCliente && !esAsesor && !esPrecalBot
  const fallo = msg.delivery_status === 'failed'
  // Del cliente no hay estado de entrega: lo recibimos nosotros. De lo que
  // salió de nuestro lado hay uno siempre, aunque sea «sin registro».
  const entrega = esCliente ? null : estadoDeEntrega(msg)

  const clases = fallo
    ? 'bg-danger-light border border-danger-border text-danger'
    : esCliente
      ? 'bg-surface-subtle border border-surface-faint text-ink'
      : esPrecalBot
        ? 'bg-info-light border border-info-border text-ink'
        : esBot
          ? 'bg-purple-light border border-purple-border text-ink'
          : 'bg-brand text-white'

  const autor = msg.sender === 'precal_cliente'
    ? 'cliente · al precalificador'
    : esCliente ? 'cliente' : esAsesor ? 'asesor' : esPrecalBot ? 'precalificador del socio' : 'IA'

  return (
    <div className={`flex ${esCliente ? 'justify-start' : 'justify-end'} ${
      nuevo ? 'animate-entrada' : ''
    }`}>
      <div
        className={`max-w-[min(74%,560px)] rounded-[14px] px-3.5 py-2.5 text-chat leading-relaxed ${clases}`}
      >
        {msg.attachment ? (
          <Adjunto attachment={msg.attachment} />
        ) : (
          <p className="whitespace-pre-wrap break-words">{msg.text}</p>
        )}
        <p className={`mt-1 flex items-center justify-end gap-1 text-pill ${
          fallo ? 'font-bold' : esBot ? 'font-bold text-purple' : 'opacity-70'
        }`}>
          {horaCorta(msg.timestamp)} ·
          {esBot && <ChispaIA />}
          {autor}
          {entrega && (
            <span title={entrega.ayuda || undefined}
                  className={entrega.tono === 'tenue' ? 'font-normal opacity-70' : undefined}>
              {` · ${entrega.etiqueta}`}
            </span>
          )}
        </p>
        {fallo && (
          <p className="mt-1 text-pill font-semibold" title={msg.failure_msg || undefined}>
            {motivoDelFallo(msg)}
          </p>
        )}
      </div>
    </div>
  )
}

// ── Sugerencia de IA ─────────────────────────────────────────────────────────

function SugerenciaIA({ texto, onUse, onDismiss }: {
  texto: string; onUse: () => void; onDismiss: () => void
}) {
  return (
    <div className="mb-2.5 rounded-notice border border-purple-border bg-purple-tint px-3.5 py-3">
      <div className="flex items-start gap-2.5">
        <svg width="16" height="16" viewBox="0 0 17 17" fill="none" className="mt-px shrink-0">
          <path d="M8.5 1 10.2 6 15 7.7l-4.8 1.7L8.5 15l-1.7-5.6L2 7.7l4.8-1.7z" fill="var(--c-purple)" />
        </svg>
        <div className="min-w-0 flex-1">
          <p className="text-meta font-bold tracking-[0.05em] text-purple">
            SUGERENCIA DE RESPUESTA · IA
          </p>
          <p className="mt-1 text-body text-ink">{texto}</p>
          <div className="mt-2.5 flex gap-2">
            {/* "Usar y editar", no "Enviar": la sugerencia NUNCA se manda sola.
                Es una decisión de diseño, no una limitación técnica. */}
            <button
              onClick={onUse}
              className="rounded-field bg-purple px-3 py-1.5 text-micro font-bold text-white"
            >
              Usar y editar
            </button>
            <button onClick={onDismiss} className="btn-row">Descartar</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Sesión devuelta al socio ─────────────────────────────────────────────────

/**
 * En lugar del compositor cuando la sesión volvió a Software. No tiene acción:
 * ninguna nuestra le llega al cliente —tampoco una plantilla, que es de
 * NUESTRA cuenta de WhatsApp—. Se habilita sola cuando Software nos hace un
 * pase nuevo. En «En vivo» va compacto, sobre la barra de monitoreo: intervenir
 * tampoco sirve para escribirle.
 */
/** «Se marcó resuelta sola · 07/10 15:42 · la atendía acasallo». */
function notaDeResuelta(detail: ConversationDetail): string {
  const cuando = fechaHoraEnLima(detail.resolved_at)
  const quien = (detail.resolved_by || '').split('@')[0]
  if (detail.resolucion_automatica) {
    return ['Se marcó resuelta sola', cuando, quien && `la atendía ${quien}`]
      .filter(Boolean).join(' · ')
  }
  return ['Resuelta', quien && `por ${quien}`, cuando].filter(Boolean).join(' ')
}

function BloqueSesionDevuelta({ aviso, compacto = false, nota = '' }: {
  aviso: AvisoDeSesionDevuelta; compacto?: boolean
  /** Una línea más al pie: quién y cuándo la resolvió, si está resuelta. */
  nota?: string
}) {
  return (
    <div className={`rounded-notice border border-surface-border bg-surface px-3.5 ${compacto ? 'mb-2.5 py-2.5' : 'py-3.5'}`}>
      <div className="flex items-start gap-2.5">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-px shrink-0 text-ink-muted" aria-hidden>
          <path d="M6 4 3 7l3 3M3.5 7h6.2a3 3 0 0 1 0 6H8"
                stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div className="min-w-0 flex-1">
          <p className="text-body font-extrabold text-ink">{aviso.titulo}</p>
          {aviso.porque && (
            <p className="mt-1 text-micro font-semibold text-ink-soft">{aviso.porque}</p>
          )}
          <p className="mt-1 text-micro text-ink-muted">{aviso.queHacer}</p>
          {nota && <p className="mt-1 text-meta font-semibold text-ink-faint">{nota}</p>}
        </div>
      </div>
    </div>
  )
}

// ── Ventana cerrada ──────────────────────────────────────────────────────────

function BloqueVentanaCerrada({ nombre, desconocida, onReactivate, reactivating,
                                sinVolverAlAgente = false, conSocio = null }: {
  nombre: string; desconocida: boolean; onReactivate: () => void; reactivating: boolean
  /** La conversación está en manos humanas: decir por qué no se puede
   *  devolver al agente, que es lo siguiente que el asesor va a buscar. */
  sinVolverAlAgente?: boolean
  /** La sesión del socio sigue siendo nuestra: por su canal no salen
   *  plantillas, así que no se ofrece el botón (`sesionSocio.ventanaCerradaConSocio`). */
  conSocio?: string | null
}) {
  return (
    <div className="rounded-notice border border-warn-border bg-warn-light px-3.5 py-3.5">
      <div className="flex items-start gap-2.5">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-px shrink-0">
          <circle cx="8" cy="8" r="6.8" stroke="var(--c-warn-dot)" strokeWidth="1.4" />
          <path d="M8 4.4V8l2.4 1.6" stroke="var(--c-warn-dot)" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <div className="min-w-0 flex-1">
          <p className="text-body font-extrabold text-warn-ink">
            {desconocida && !conSocio ? 'El cliente aún no escribió' : 'Ventana de 24 h cerrada'}
          </p>
          <p className="mt-1 text-micro text-warn-ink">
            {conSocio
              ?? (desconocida
                ? `${nombre} todavía no envió ningún mensaje. WhatsApp solo permite iniciar la conversación con una plantilla aprobada.`
                : `${nombre} no escribe hace más de 24 h. WhatsApp bloquea los mensajes libres fuera de la ventana: envía una plantilla aprobada o espera a que el cliente escriba primero.`)}
          </p>
          {sinVolverAlAgente && (
            <p className="mt-1 text-micro text-warn-ink">
              Tampoco se puede devolver al agente todavía: para retomarla tiene que
              escribirle al cliente, y eso también necesita la ventana abierta.
            </p>
          )}
          {!conSocio && (
            <div className="mt-2.5 flex items-center gap-2.5">
              <button
                onClick={onReactivate}
                disabled={reactivating}
                className="rounded-field bg-warn px-3 py-1.5 text-micro font-bold text-white disabled:opacity-50"
              >
                {reactivating ? 'Enviando…' : 'Reactivar con plantilla aprobada'}
              </button>
              <span className="font-mono text-meta text-warn-ink opacity-80">
                reengagement_pgh_v1
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Monitoreo (En vivo) ──────────────────────────────────────────────────────

function OjoIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M1 8s2.6-4.2 7-4.2S15 8 15 8s-2.6 4.2-7 4.2S1 8 1 8z"
            stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <circle cx="8" cy="8" r="1.9" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}

/**
 * Barra de monitoreo — ocupa el lugar del compositor en modo solo lectura.
 *
 * El azul del token `info` no es decorativo: es el mismo de la burbuja
 * del asesor humano y de los avisos informativos. Marca todo lo que es
 * intervención humana sobre el flujo del bot.
 */
function BarraMonitoreo({
  asesor, conduceIA = false, sinCanal = false, onIntervene, onReassign, busy, esMia,
}: {
  asesor: string | null
  /** Asignada, pero la conduce el agente: nadie la está atendiendo todavía. */
  conduceIA?: boolean
  /**
   * La sesión volvió al precalificador del socio: intervenir o responder no le
   * llegaría al cliente, así que no se ofrece. Queda «Reasignar», que solo
   * cambia quién revisa el expediente (06-oct-2026).
   */
  sinCanal?: boolean
  onIntervene: () => void
  onReassign: () => void
  busy: boolean
  esMia: boolean
}) {
  const enCola = !asesor

  if (sinCanal) {
    return (
      <div className="rounded-notice border border-surface-border bg-surface px-3.5 py-3">
        <p className="text-body font-extrabold text-ink">Solo lectura · ya no hay nada que atender</p>
        <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">
          El cliente está en el precalificador del socio: ni intervenir ni responder le
          llegaría. Reasignar solo cambia quién revisa el expediente.
        </p>
        <div className="mt-2.5">
          <button
            onClick={onReassign}
            disabled={busy}
            className="rounded-btn border border-surface-border px-3.5 py-2 text-micro font-bold text-ink-soft disabled:opacity-50"
          >
            Reasignar
          </button>
        </div>
      </div>
    )
  }

  // Ya es tuya: «En vivo» sigue siendo de solo lectura —es para mirar al
  // equipo—, así que lo que falta es llevarte a donde se contesta.
  if (esMia) {
    return (
      <div className="rounded-notice border border-brand-border bg-brand-light px-3.5 py-3.5">
        <div className="flex items-start gap-3">
          <span className="mt-px shrink-0 text-brand-hover"><OjoIcon size={17} /></span>
          <div className="min-w-0 flex-1">
            <p className="text-body font-extrabold text-brand-ink">Esta conversación es tuya</p>
            <p className="mt-1 text-[12px] leading-relaxed text-brand-ink opacity-85">
              Acá la ves en solo lectura. Para escribirle al cliente, ábrela en tu bandeja.
            </p>
            <div className="mt-2.5 flex items-center gap-2">
              <button
                onClick={onIntervene}
                className="rounded-btn bg-brand px-3.5 py-2 text-micro font-bold text-white"
              >
                Responder desde Mi bandeja
              </button>
              <button
                onClick={onReassign}
                disabled={busy}
                className="rounded-btn border border-brand-border px-3.5 py-2 text-micro font-bold text-brand-hover disabled:opacity-50"
              >
                Reasignar
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-notice border border-info-border bg-info-light px-3.5 py-3.5">
      <div className="flex items-start gap-3">
        <span className="mt-px shrink-0 text-info"><OjoIcon size={17} /></span>
        <div className="min-w-0 flex-1">
          <p className="text-body font-extrabold text-info">
            {enCola
              ? 'Solo lectura · sin asesor asignado'
              : conduceIA
                ? `Solo lectura · asignada a ${asesor.split('@')[0]} · conduce el agente IA`
                : `Solo lectura · atiende ${asesor.split('@')[0]}`}
          </p>
          <p className="mt-1 text-[12px] leading-relaxed text-info opacity-85">
            {enCola
              ? 'Esta conversación está en cola sin asesor. Asígnala a alguien disponible o tómala tú.'
              : conduceIA
                ? 'El agente IA la sigue conduciendo; el reparto ya la dejó a nombre de un asesor. '
                  + 'Si intervienes, la tomas tú y el agente deja de responder.'
                : 'Estás viendo la conversación en vivo. Interviene solo si necesitas tomar la atención — '
                  + 'el asesor la pierde de su bandeja y queda registrado en auditoría.'}
          </p>
          <div className="mt-2.5 flex items-center gap-2">
            <button
              onClick={onIntervene}
              disabled={busy}
              className="rounded-btn bg-info px-3.5 py-2 text-micro font-bold text-white disabled:opacity-50"
            >
              {busy
                ? 'Tomando…'
                : enCola ? 'Tomar la conversación' : 'Intervenir en la conversación'}
            </button>
            <button
              onClick={onReassign}
              disabled={busy}
              className="rounded-btn border border-info-border px-3.5 py-2 text-micro font-bold text-info disabled:opacity-50"
            >
              Reasignar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
